import React, { useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import LoginForm from '../components/Auth/LoginForm';
import TrovaBrand from '../components/Auth/TrovaBrand';
import { useAuth } from '../contexts/AuthContext';

const LoginPage: React.FC = () => {
  const { user, loading } = useAuth();
  const [oauthLoading, setOauthLoading] = useState<'google' | 'microsoft' | null>(null);
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo') || '/dashboard';

  const handleOAuthClick = (provider: 'google' | 'microsoft') => {
    setOauthLoading(provider);
    sessionStorage.setItem('authReturnTo', returnTo);
    window.location.href = `/api/auth/${provider}`;
  };

  // If user is already logged in, redirect to intended destination
  if (user && !loading) {
    return <Navigate to={returnTo} replace />;
  }
  
  return (
    <div className="login-page">
      <Helmet>
        <title>Sign In</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="login-container">
        <div className="login-header">
          <TrovaBrand />
          <p className="login-subtitle">
            Sign in to access your executive hiring tools
          </p>
        </div>

        <div className="login-form-container">
          <LoginForm />
          
          <div className="oauth-divider">
            <div className="oauth-divider-line"></div>
            <div className="oauth-divider-text">
              <span>Or continue with</span>
            </div>
          </div>

          <div className="oauth-buttons">
            <button 
              type="button"
              onClick={() => handleOAuthClick('google')} 
              className="oauth-button"
              disabled={oauthLoading !== null}
            >
              {oauthLoading === 'google' ? (
                <svg className="oauth-spinner" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeDasharray="31.4 31.4" />
                </svg>
              ) : (
                <>
                  <span className="sr-only">Sign in with Google</span>
                  <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z" />
                  </svg>
                </>
              )}
            </button>

            <button 
              type="button"
              onClick={() => handleOAuthClick('microsoft')} 
              className="oauth-button"
              disabled={oauthLoading !== null}
            >
              {oauthLoading === 'microsoft' ? (
                <svg className="oauth-spinner" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeDasharray="31.4 31.4" />
                </svg>
              ) : (
                <>
                  <span className="sr-only">Sign in with Microsoft</span>
                  <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M11.4 24H0V12.6h11.4V24zM24 24H12.6V12.6H24V24zM11.4 11.4H0V0h11.4v11.4zM24 11.4H12.6V0H24v11.4z" />
                  </svg>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
